import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import { ListItemIcon, ListItemText } from "@material-ui/core";
import { Check, Replay, SwapHoriz, VisibilityOff, DeleteOutline, SettingsInputAntenna, Minimize } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import TrocarConexaoModal from "../TrocarConexaoModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FlutuantesAcoesContext } from "../../context/Flutuantes/FlutuantesContext";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles(theme => ({
	menuItemFinalizar: {
		"&:hover": {
			backgroundColor: "rgba(30,152,85,0.12)",
			color: "#1E9855",
			"& .MuiListItemIcon-root": {
				color: "#1E9855"
			}
		},
	},
	menuItemRetornar: {
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "#f3f4f6",
		},
	},
	menuItemTransferir: {
		"&:hover": {
			backgroundColor: "rgba(33,150,243,0.1)",
			color: "#2196f3",
			"& .MuiListItemIcon-root": {
				color: "#2196f3"
			}
		},
	},
	menuItemConexao: {
		"&:hover": {
			backgroundColor: "rgba(30,152,85,0.1)",
			color: "#1E9855",
			"& .MuiListItemIcon-root": {
				color: "#1E9855"
			}
		},
	},
	menuItemOcultar: {
		"&:hover": {
			backgroundColor: "rgba(244,67,54,0.1)",
			color: "#f44336",
			"& .MuiListItemIcon-root": {
				color: "#f44336"
			}
		},
	},
	iconRoot: {
		minWidth: 36
	}
}));

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl, handleUpdateTicketStatus }) => {
	const classes = useStyles();
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [contactConfirmationOpen, setContactConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [trocarConexaoOpen, setTrocarConexaoOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { minimizarConversa } = useContext(FlutuantesAcoesContext) || {};

	// Vira uma bolinha flutuante (tipo Messenger) que acompanha o atendente
	// pelas outras páginas do sistema.
	const handleMinimizar = () => {
		handleClose();
		if (!minimizarConversa) return;
		minimizarConversa({
			id: ticket.id,
			nome: ticket.contact?.name,
			foto: ticket.contact?.profilePicUrl,
		});
		history.push("/atendimentos");
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteContact = async () => {
		try {
			await api.delete(`/contacts/${ticket.contact.id}`);
			history.push("/atendimentos");
		} catch (err) {
			toastError(err);
		}
	};

	// Ocultar tira o atendimento da lista e nada mais: a conversa continua
	// inteira no banco e volta pela busca. E a mesma acao do menu da lista e a
	// que o sistema faz sozinho com os finalizados de mais de uma semana.
	const handleDeleteTicket = async () => {
		try {
			await api.put(`/tickets/${ticket.id}/ocultar`, { oculto: true });
			history.push("/atendimentos");
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleOpenTrocarConexao = () => {
		setTrocarConexaoOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				{}
				{ticket.status === "closed" && (
					<MenuItem
						onClick={(e) => {
							handleUpdateTicketStatus(e, "open", user?.id);
							handleClose();
						}}
						className={classes.menuItemFinalizar}
					>
						<ListItemIcon className={classes.iconRoot}>
							<Replay fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={i18n.t("messagesList.header.buttons.reopen")} />
					</MenuItem>
				)}

				{(ticket.status === "open" || ticket.status === "pending") && (
					<>
						<MenuItem 
							onClick={(e) => {
								handleUpdateTicketStatus(e, "closed", user?.id);
								handleClose();
							}} 
							className={classes.menuItemFinalizar}
						>
							<ListItemIcon className={classes.iconRoot}>
								<Check fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={i18n.t("messagesList.header.buttons.resolve")} />
						</MenuItem>
						
						<Can
							role={user.profile}
							perform="ticket-options:deleteTicket"
							yes={() => ticket.status === "open" && (
								<MenuItem 
									onClick={(e) => {
										handleUpdateTicketStatus(e, "pending", null);
										handleClose();
									}} 
									className={classes.menuItemRetornar}
								>
									<ListItemIcon className={classes.iconRoot}>
										<Replay fontSize="small" />
									</ListItemIcon>
									<ListItemText primary={i18n.t("messagesList.header.buttons.return")} />
								</MenuItem>
							)}
						/>
					</>
				)}

				<MenuItem onClick={handleMinimizar} className={classes.menuItemRetornar}>
					<ListItemIcon className={classes.iconRoot}>
						<Minimize fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Minimizar" />
				</MenuItem>

				<MenuItem onClick={handleOpenTransferModal} className={classes.menuItemTransferir}>
					<ListItemIcon className={classes.iconRoot}>
						<SwapHoriz fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("ticketOptionsMenu.transfer")} />
				</MenuItem>

				{/*
					Trocar o numero pelo qual o atendimento continua. Serve para o
					atendimento que ficou sem conexao (a dele foi excluida) e tambem
					para mudar de numero no meio da conversa. Disponivel para todo
					mundo: quem esta com o cliente na mao e quem precisa resolver.
				*/}
				<MenuItem onClick={handleOpenTrocarConexao} className={classes.menuItemConexao}>
					<ListItemIcon className={classes.iconRoot}>
						<SettingsInputAntenna fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Trocar conexão" />
				</MenuItem>
				
				{/* Ocultar disponivel em qualquer status, inclusive na fila de espera */}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal} className={classes.menuItemOcultar}>
							<ListItemIcon className={classes.iconRoot}>
								<VisibilityOff fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={i18n.t("ticketOptionsMenu.delete")} />
						</MenuItem>
					)}
				/>

				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={() => { setContactConfirmationOpen(true); handleClose(); }} className={classes.menuItemOcultar}>
							<ListItemIcon className={classes.iconRoot}>
								<DeleteOutline fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Deletar contato" />
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`Ocultar o atendimento de ${ticket.contact?.name || "cliente"} (nº ${ticket.id})?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				O atendimento sai da sua lista. A conversa continua guardada e volta
				pela busca, ou quando o cliente escrever de novo.
			</ConfirmationModal>
			<ConfirmationModal
				title={`Excluir Contato ${ticket.contact?.name || ""}?`}
				open={contactConfirmationOpen}
				onClose={setContactConfirmationOpen}
				onConfirm={handleDeleteContact}
			>
				Tem certeza que deseja excluir o contato permanentemente? Isso irá apagar todas as conversas e o cliente será removido da base.
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
			/>
			<TrocarConexaoModal
				modalOpen={trocarConexaoOpen}
				onClose={() => { if (isMounted.current) setTrocarConexaoOpen(false); }}
				ticketId={ticket.id}
				whatsappIdAtual={ticket.whatsappId}
			/>
		</>
	);
};

export default TicketOptionsMenu;
